import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, Select, Space, Table, message } from 'antd';
import {
  CreditAccountRow,
  listCreditAccounts,
  receiveCreditPayment,
} from '../../services/posService';

type CreditBookModalProps = {
  open: boolean;
  onClose: () => void;
};

export const CreditBookModal: React.FC<CreditBookModalProps> = ({ open, onClose }) => {
  const [rows, setRows] = useState<CreditAccountRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<'CASH' | 'BANK_TRANSFER'>('CASH');
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listCreditAccounts();
      setRows(data);
    } catch (err: any) {
      message.error(err?.message || 'Could not load credit accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      void load();
    }
  }, [open]);

  const selected = rows.find((row) => row.id === customerId);

  const receive = async () => {
    if (!customerId) {
      message.warning('Choose a customer');
      return;
    }
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      message.warning('Enter the amount received');
      return;
    }
    if (method === 'BANK_TRANSFER' && !reference.trim()) {
      message.warning('Enter the journal number');
      return;
    }
    setSaving(true);
    try {
      const result = await receiveCreditPayment({
        customer_id: customerId,
        amount: value,
        payment_method: method,
        reference_number: reference.trim() || undefined,
      });
      message.success(`Received. Balance is now Nu. ${Number(result.balance).toFixed(2)}`);
      setAmount('');
      setReference('');
      await load();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || err?.message || 'Could not record payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Credit book"
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
      destroyOnClose
    >
      <Table
        size="small"
        loading={loading}
        rowKey="id"
        pagination={false}
        dataSource={rows}
        locale={{ emptyText: 'No outstanding credit' }}
        columns={[
          { title: 'Customer', dataIndex: 'name' },
          { title: 'Phone', dataIndex: 'phone' },
          {
            title: 'Owes (Nu.)',
            dataIndex: 'balance',
            align: 'right',
            render: (value: number) => Number(value).toFixed(2),
          },
        ]}
      />
      <Space direction="vertical" style={{ width: '100%', marginTop: 16 }} size={8}>
        <Select
          placeholder="Customer paying now"
          style={{ width: '100%' }}
          value={customerId ?? undefined}
          onChange={(value) => setCustomerId(value)}
          options={rows.map((row) => ({
            value: row.id,
            label: `${row.name} — Nu. ${Number(row.balance).toFixed(2)}`,
          }))}
        />
        <Input
          placeholder="Amount received"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="decimal"
        />
        <Select
          value={method}
          onChange={(value) => setMethod(value)}
          options={[
            { value: 'CASH', label: 'Cash' },
            { value: 'BANK_TRANSFER', label: 'Bank / mBoB' },
          ]}
        />
        {method === 'BANK_TRANSFER' && (
          <Input
            placeholder="Journal number"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        )}
        <Button type="primary" loading={saving} onClick={() => void receive()} disabled={!selected}>
          Record payment
        </Button>
      </Space>
    </Modal>
  );
};
